import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HERO_SLIDES } from '../data/content';
import './Hero.css';

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const t0 = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t0);
  }, []);

  // Auto-slide
  useEffect(() => {
    const t = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrent(i => (i + 1) % HERO_SLIDES.length);
        setFading(false);
      }, 500);
    }, 5500);
    return () => clearInterval(t);
  }, []);

  // 3D tilt on mouse move
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (rect.width / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);
      const rotateX = (-dy * 12).toFixed(2);
      const rotateY = (dx * 12).toFixed(2);
      card.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.04,1.04,1.04)`;
      card.style.transition = 'transform 0.08s linear';
      const shine = card.querySelector('.pcb-shine');
      if (shine) {
        shine.style.background = `radial-gradient(circle at ${((dx + 1) / 2) * 100}% ${((dy + 1) / 2) * 100}%, rgba(255,220,80,0.12) 0%, transparent 65%)`;
      }
    };

    const handleMouseLeave = () => {
      card.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
      card.style.transition = 'transform 0.6s cubic-bezier(0.16,1,0.3,1)';
    };

     
    const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const rect = card.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (touch.clientX - cx) / (rect.width / 2);
    const dy = (touch.clientY - cy) / (rect.height / 2);
    const rotateX = (-dy * 8).toFixed(2);   // softer on mobile
    const rotateY = (dx * 8).toFixed(2);
    card.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02,1.02,1.02)`;
    card.style.transition = 'transform 0.08s linear';
  };

  const handleTouchEnd = () => {
    card.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    card.style.transition = 'transform 0.6s cubic-bezier(0.16,1,0.3,1)';
  };

    const parent = card.parentElement;
    parent.addEventListener('mousemove', handleMouseMove);
    parent.addEventListener('mouseleave', handleMouseLeave);
    parent.addEventListener('touchmove', handleTouchMove, { passive: true });
    parent.addEventListener('touchend', handleTouchEnd);   
    return () => {
      parent.removeEventListener('mousemove', handleMouseMove);
      parent.removeEventListener('mouseleave', handleMouseLeave);
      parent.removeEventListener('touchmove', handleTouchMove);
      parent.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const goTo = (i) => {
    if (i === current) return;
    setFading(true);
    setTimeout(() => { setCurrent(i); setFading(false); }, 500);
  };

  const slide = HERO_SLIDES[current];

  return (
    <section id="home" className={`hero${loaded ? ' hero--loaded' : ''}`}>

      <div className="hero-bg">
        <div className="hero-bg-img" />
        <div className="hero-bg-overlay" />
        <div className="hero-scanlines" />
      </div>
      <div className="hero-grid" />
      <div className="hero-streak hero-streak-1" />
      <div className="hero-streak hero-streak-2" />
      <div className="hero-streak hero-streak-3" />
      <div className="hero-particles" aria-hidden="true">
        {[...Array(12)].map((_, i) => (
          <div key={i} className={`hero-particle hero-particle--${i + 1}`} />
        ))}
      </div>

      <div className="hero-inner">

        {/* Left column */}
        <div className="hero-content">
          <div className={`hero-badge${fading ? ' is-fading' : ''}`}>
            <span className="hero-badge-pulse" />
            <span className="hero-badge-text">{slide.tag}</span>
          </div>
          <h1 className={`hero-heading${fading ? ' is-fading' : ''}`}>
            {slide.heading}
          </h1>
          <p className={`hero-sub${fading ? ' is-fading' : ''}`}>
            {slide.sub}
          </p>
          <div className="hero-ctas">
            <Link to="/services" className="hero-btn hero-btn--primary">
              Explore Services
              <svg className="hero-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
            <Link to="/contact" className="hero-btn hero-btn--ghost">
              Get In Touch
            </Link>
          </div>
          <div className="hero-dots" role="tablist">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i} role="tab"
                aria-selected={i === current}
                aria-label={`Slide ${i + 1}`}
                className={`hero-dot${i === current ? ' hero-dot--active' : ''}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        </div>

        {/* Right column – Gold PCB photo with DWB chip overlay */}
        <div className="hero-art">
          <div className="pcb-photo-wrap">

            <div className="pcb-outer-ring" />

            <div className="pcb-photo-card" ref={cardRef}>

              {/* Corner accents */}
              <span className="pcb-corner pcb-corner--tl" />
              <span className="pcb-corner pcb-corner--tr" />
              <span className="pcb-corner pcb-corner--bl" />
              <span className="pcb-corner pcb-corner--br" />

              {/* Gold glowing circuit board image */}
              <img
                src="https://images.unsplash.com/photo-1635070041409-4a4b7c94e9ca?w=900&q=90&auto=format&fit=crop"
                alt="Gold PCB circuit board"
                className="pcb-photo-img"
                onError={e => {
                  // fallback to another gold-trace PCB image
                 e.target.src = 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=900&q=90&auto=format&fit=crop';
                }}
              />

              {/* Shine overlay */}
              <div className="pcb-shine" />

              {/* Bottom fade */}
              <div className="pcb-photo-fade" />

              {/* ── Centered DWB Chip Overlay ── */}
              <div className="dwb-chip">
                {/* Chip body */}
                <div className="dwb-chip-body">
                  {/* Pin rows top & bottom */}
                  <div className="dwb-chip-pins dwb-chip-pins--top">
                    {[...Array(5)].map((_, i) => <span key={i} className="dwb-pin" />)}
                  </div>
                  <div className="dwb-chip-pins dwb-chip-pins--bottom">
                    {[...Array(5)].map((_, i) => <span key={i} className="dwb-pin" />)}
                  </div>
                  {/* Pin rows left & right */}
                  <div className="dwb-chip-pins dwb-chip-pins--left">
                    {[...Array(4)].map((_, i) => <span key={i} className="dwb-pin dwb-pin--side" />)}
                  </div>
                  <div className="dwb-chip-pins dwb-chip-pins--right">
                    {[...Array(4)].map((_, i) => <span key={i} className="dwb-pin dwb-pin--side" />)}
                  </div>
                  {/* Chip face */}
                  <div className="dwb-chip-face">
                    <div className="dwb-chip-notch" />
                    <div className="dwb-chip-label">DWB</div>
                    <div className="dwb-chip-sub">TECH</div>
                    {/* Inner glow grid */}
                    <div className="dwb-chip-grid" />
                  </div>
                </div>
                {/* Pulse rings */}
                <div className="dwb-chip-ring dwb-chip-ring--1" />
                <div className="dwb-chip-ring dwb-chip-ring--2" />
              </div>

              {/* Scan line */}
              <div className="pcb-scanline" />

              {/* Stat badges */}
              
              <div className="pcb-stat pcb-stat--br">
             
              </div>
            </div>

            <div className="pcb-dot pcb-dot--1" />
            <div className="pcb-dot pcb-dot--2" />
            <div className="pcb-dot pcb-dot--3" />
          </div>
        </div>
      </div>

      <div className="hero-scroll">
        <div className="hero-scroll-mouse">
          <div className="hero-scroll-wheel" />
        </div>
        <span>Scroll</span>
      </div>
    </section>
  );
}